/**
 * Unified z-index management for shift calendar components
 * Prevents stacking conflicts and ensures proper layering
 */

export const Z_INDEX_LAYERS = {
  // Base layer - calendar grid and background elements
  CALENDAR_BASE: 1,
  GRID_LINES: 2,
  OPENING_HOURS: 3,

  // Grid interaction layer - BELOW shifts so shifts receive clicks first
  // This handles clicks/hover on empty grid areas
  GRID_INTERACTION: 4,

  // Time indicators
  CURRENT_TIME_LINE: 5,

  TIME_HEADER: 9,

  // Shift elements
  SHIFT_NORMAL: 10,
  SHIFT_HOVER: 15,
  SHIFT_SELECTED: 20,

  // Shift creation preview - above normal shifts, below selected/dragging
  SHIFT_CREATION_PREVIEW: 18,
  CREATION_OVERLAY: 18, // Same as preview - above normal shifts but allows click-through

  // Events
  CALENDAR_EVENT: 15,

  // Interactive overlays
  DROP_ZONE: 25,
  TEMPLATE_OVERLAY: 30,
  SHIFT_DRAGGING: 50, // Reduced from 100 to allow hover events to pass through
  
  // UI components
  STICKY_HEADER: 10,
  DROPDOWN_MENU: 100,
  POPOVER: 100,
  TOOLTIP: 9000,
  
  // Custom day template popup
  TEMPLATE_POPUP: 21,
  
  // Collaborative features
  COLLABORATIVE_INDICATORS: 50,
  
  // Offline/sync indicators
  OFFLINE_INDICATOR: 50,
  SYNC_STATUS: 50,
  
  // Mobile drag overlay
  MOBILE_DRAG_OVERLAY: 1000,
  MOBILE_DRAG_PREVIEW: 1001,
  
  // Toast notifications (highest)
  TOAST: 9999,
} as const;

/**
 * CSS class names for z-index layers
 */
export const Z_INDEX_CLASSES = {
  CALENDAR_BASE: 'z-[1]',
  GRID_LINES: 'z-[2]',
  OPENING_HOURS: 'z-[3]',
  GRID_INTERACTION: 'z-[4]',
  CURRENT_TIME_LINE: 'z-[5]',
  TIME_HEADER: 'z-[9]',
  SHIFT_NORMAL: 'z-[10]',
  SHIFT_HOVER: 'z-[15]',
  CALENDAR_EVENT: 'z-[15]',
  SHIFT_CREATION_PREVIEW: 'z-[18]',
  CREATION_OVERLAY: 'z-[18]', // **CURSOR FOLLOW FIX**: Above normal shifts (10) but below selected (20)
  SHIFT_SELECTED: 'z-[20]',
  DROP_ZONE: 'z-[25]',
  TEMPLATE_OVERLAY: 'z-[30]',
  SHIFT_DRAGGING: 'z-[50]', // Reduced from z-[100] to allow hover events to pass through
  STICKY_HEADER: 'z-[10]',
  DROPDOWN_MENU: 'z-[100]',
  POPOVER: 'z-[100]',
  TOOLTIP: 'z-[9000]',
  TEMPLATE_POPUP: 'z-[21]',
  COLLABORATIVE_INDICATORS: 'z-[50]',
  OFFLINE_INDICATOR: 'z-[50]',
  SYNC_STATUS: 'z-[50]',
  MOBILE_DRAG_OVERLAY: 'z-[1000]',
  MOBILE_DRAG_PREVIEW: 'z-[1001]',
  TOAST: 'z-[9999]',
} as const;

/**
 * Helper function to get z-index value
 */
export const getZIndex = (layer: keyof typeof Z_INDEX_LAYERS): number => {
  return Z_INDEX_LAYERS[layer];
};

/**
 * Helper function to get z-index class
 */
export const getZIndexClass = (layer: keyof typeof Z_INDEX_CLASSES): string => {
  return Z_INDEX_CLASSES[layer];
};

/**
 * Generate CSS custom properties for z-index layers
 */
export const generateZIndexCSSVariables = (): Record<string, number> => {
  const cssVariables: Record<string, number> = {};
  
  Object.entries(Z_INDEX_LAYERS).forEach(([key, value]) => {
    const cssVarName = `--z-index-${key.toLowerCase().replace(/_/g, '-')}`;
    cssVariables[cssVarName] = value;
  });
  
  return cssVariables;
};

/**
 * Validate z-index layering to prevent conflicts
 */
export const validateZIndexLayers = (): { isValid: boolean; conflicts: string[] } => {
  const conflicts: string[] = [];
  const values = Object.values(Z_INDEX_LAYERS);
  const duplicates = values.filter((value, index, arr) => arr.indexOf(value) !== index);
  
  if (duplicates.length > 0) {
    duplicates.forEach(duplicate => {
      const layers = Object.entries(Z_INDEX_LAYERS)
        .filter(([, value]) => value === duplicate)
        .map(([key]) => key);
      conflicts.push(`Duplicate z-index ${duplicate} used by: ${layers.join(', ')}`);
    });
  }
  
  // Check for logical ordering issues
  if (Z_INDEX_LAYERS.SHIFT_NORMAL >= Z_INDEX_LAYERS.SHIFT_HOVER) {
    conflicts.push('SHIFT_HOVER should have higher z-index than SHIFT_NORMAL');
  }
  
  if (Z_INDEX_LAYERS.SHIFT_HOVER >= Z_INDEX_LAYERS.SHIFT_SELECTED) {
    conflicts.push('SHIFT_SELECTED should have higher z-index than SHIFT_HOVER');
  }
  
  if (Z_INDEX_LAYERS.SHIFT_SELECTED >= Z_INDEX_LAYERS.SHIFT_DRAGGING) {
    conflicts.push('SHIFT_DRAGGING should have higher z-index than SHIFT_SELECTED');
  }
  
  return {
    isValid: conflicts.length === 0,
    conflicts
  };
};